<template>
  <div class="conversation-shell" :class="{ 'conversation-shell--embedded': embedded }">
    <div class="conversation-header">
      <div class="conversation-title-group">
        <div class="conversation-title-main">会话记录</div>
        <div class="conversation-title-sub">
          <span>搜索词：{{ searchWord || '-' }}</span>
          <span>关键词：{{ keyword || '-' }}</span>
          <span>消息数：{{ messageCount }}</span>
        </div>
      </div>
    </div>

    <div v-loading="loading" class="conversation-stream" :class="{ 'conversation-stream--embedded': embedded }">
      <template v-if="messages.length">
        <div
          v-for="item in messages"
          :key="item.id"
          :class="[
            'conversation-item',
            `conversation-item--${item.role}`,
            { 'conversation-item--match': messageMatches(item) }
          ]"
        >
          <div v-if="item.role !== 'system'" class="conversation-avatar">
            {{ item.role === 'customer' ? '客' : '我' }}
          </div>
          <div :class="['conversation-content', `conversation-content--${item.role}`]">
            <div v-if="item.role === 'system'" class="conversation-system-line">
              <span class="conversation-system-text">
                <template v-for="(part, index) in highlightParts(item.content)">
                  <mark
                    v-if="part.match"
                    :key="`system-${item.id}-${index}`"
                    class="conversation-highlight"
                  >{{ part.text }}</mark>
                  <span v-else :key="`system-text-${item.id}-${index}`">{{ part.text }}</span>
                </template>
              </span>
              <span v-if="item.displayTime" class="conversation-system-time">{{ item.displayTime }}</span>
            </div>
            <template v-else>
              <div class="conversation-stack">
                <div v-if="item.displayTime" class="conversation-meta">
                  <span class="conversation-time">{{ item.displayTime }}</span>
                </div>
                <div class="conversation-bubble">
                  <a
                    v-if="item.mediaType === 'image' && item.mediaUrl"
                    :href="item.mediaUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="conversation-image-link"
                  >
                    <img
                      :src="item.mediaUrl"
                      :alt="item.content || '聊天图片'"
                      class="conversation-image"
                    />
                  </a>
                  <span
                    v-else
                    class="conversation-text"
                  >
                    <template v-for="(part, index) in highlightParts(item.content)">
                      <mark
                        v-if="part.match"
                        :key="`text-${item.id}-${index}`"
                        class="conversation-highlight"
                      >{{ part.text }}</mark>
                      <span v-else :key="`text-plain-${item.id}-${index}`">{{ part.text }}</span>
                    </template>
                  </span>
                  <div
                    v-if="item.messageType === 'TEXT' && item.formFields && item.formFields.length"
                    class="conversation-form-card"
                  >
                    <div
                      v-for="(field, index) in item.formFields"
                      :key="`${item.id}-field-${index}`"
                      class="conversation-form-row"
                    >
                      <div class="conversation-form-label">{{ field.label || '字段' }}</div>
                      <div class="conversation-form-value">
                        <a
                          v-if="field.url"
                          :href="field.url"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="conversation-form-link"
                        >{{ field.value || field.url }}</a>
                        <span v-else>{{ field.value || '-' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
      <el-empty v-else description="暂无会话消息" :image-size="88" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaiduConversationPanel',
  props: {
    loading: { type: Boolean, default: false },
    conversation: {
      type: Object,
      default: () => ({})
    },
    embedded: { type: Boolean, default: false },
    highlightText: { type: String, default: '' }
  },
  computed: {
    messages() {
      return Array.isArray(this.conversation.messages) ? this.conversation.messages : [];
    },
    clueId() {
      return this.conversation.clueId || '';
    },
    keyword() {
      return this.conversation.keyword || '';
    },
    searchWord() {
      return this.conversation.searchWord || '';
    },
    messageCount() {
      return this.messages.length;
    },
    normalizedHighlightText() {
      return String(this.highlightText || '').trim().toLowerCase();
    }
  },
  watch: {
    messages() {
      this.scrollToFirstMatch();
    },
    normalizedHighlightText() {
      this.scrollToFirstMatch();
    },
    loading(value) {
      if (!value) this.scrollToFirstMatch();
    }
  },
  methods: {
    messageMatches(item) {
      if (!this.normalizedHighlightText || !item) return false;
      return String(item.content || '').toLowerCase().includes(this.normalizedHighlightText);
    },
    highlightParts(text) {
      const source = String(text || '');
      const needle = String(this.highlightText || '').trim();
      if (!source || !needle) return [{ text: source, match: false }];
      const lowerSource = source.toLowerCase();
      const lowerNeedle = needle.toLowerCase();
      const parts = [];
      let start = 0;
      let index = lowerSource.indexOf(lowerNeedle);
      while (index !== -1) {
        if (index > start) {
          parts.push({ text: source.slice(start, index), match: false });
        }
        parts.push({ text: source.slice(index, index + needle.length), match: true });
        start = index + needle.length;
        index = lowerSource.indexOf(lowerNeedle, start);
      }
      if (start < source.length) {
        parts.push({ text: source.slice(start), match: false });
      }
      return parts.length ? parts : [{ text: source, match: false }];
    },
    scrollToFirstMatch() {
      if (!this.normalizedHighlightText) return;
      this.$nextTick(() => {
        const root = this.$el && this.$el.querySelector ? this.$el.querySelector('.conversation-stream') : null;
        const target = root && root.querySelector ? root.querySelector('.conversation-item--match') : null;
        if (!root || !target) return;
        root.scrollTop = Math.max(0, target.offsetTop - root.offsetTop - 18);
      });
    }
  }
};
</script>

<style scoped>
.conversation-shell {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 560px;
}

.conversation-shell--embedded {
  min-height: auto;
}

.conversation-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-radius: 22px;
  background:
    radial-gradient(circle at top left, rgba(149, 196, 113, 0.18), transparent 42%),
    linear-gradient(135deg, #f7fbf2 0%, #edf6e4 100%);
  border: 1px solid rgba(141, 181, 110, 0.28);
}

.conversation-title-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.conversation-title-main {
  font-size: 22px;
  font-weight: 700;
  color: #1f2d1a;
  letter-spacing: 0.02em;
}

.conversation-title-sub {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  color: #5d6f53;
  font-size: 13px;
}

.conversation-stream {
  flex: 1;
  padding: 24px 22px;
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(245, 247, 242, 0.98) 0%, rgba(234, 239, 230, 0.98) 100%);
  border: 1px solid #dce6d4;
  overflow-y: auto;
  max-height: 62vh;
}

.conversation-stream--embedded {
  max-height: calc(100vh - 220px);
}

.conversation-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 18px;
  scroll-margin-top: 18px;
}

.conversation-item--customer {
  flex-direction: row;
}

.conversation-item--service {
  flex-direction: row-reverse;
}

.conversation-item--system {
  justify-content: center;
}

.conversation-avatar {
  width: 38px;
  height: 38px;
  border-radius: 14px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #7fb069 0%, #4f7d43 100%);
  box-shadow: 0 12px 22px rgba(79, 125, 67, 0.18);
}

.conversation-item--customer .conversation-avatar {
  background: linear-gradient(135deg, #8ba4f9 0%, #5f78d6 100%);
  box-shadow: 0 12px 22px rgba(95, 120, 214, 0.18);
}

.conversation-content {
  display: flex;
  flex-direction: column;
  max-width: 70%;
  gap: 6px;
}

.conversation-item--service .conversation-content {
  align-items: flex-end;
}

.conversation-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
}

.conversation-item--service .conversation-stack {
  align-items: flex-end;
}

.conversation-meta {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  color: #74836a;
  font-size: 12px;
}

.conversation-bubble {
  display: inline-flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  padding: 14px 16px;
  border-radius: 20px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  box-shadow: 0 18px 30px rgba(102, 124, 93, 0.10);
}

.conversation-item--customer .conversation-bubble {
  background: #ffffff;
  color: #24311f;
  border-top-left-radius: 8px;
}

.conversation-item--service .conversation-bubble {
  background: linear-gradient(135deg, #b9e97b 0%, #95cf52 100%);
  color: #1f2d1a;
  border-top-right-radius: 8px;
}

.conversation-text {
  font-size: 14px;
  display: inline-block;
  max-width: 100%;
}

.conversation-highlight {
  padding: 0 2px;
  border-radius: 4px;
  background: #fff176;
  color: inherit;
  font-weight: 700;
}

.conversation-image-link {
  display: block;
  max-width: min(320px, 58vw);
  line-height: 0;
}

.conversation-image {
  display: block;
  width: auto;
  max-width: 100%;
  max-height: 260px;
  border-radius: 12px;
  object-fit: contain;
  background: rgba(255, 255, 255, 0.58);
}

.conversation-form-card {
  width: min(360px, 100%);
  overflow: hidden;
  border-radius: 14px;
  border: 1px solid rgba(31, 45, 26, 0.10);
  background: rgba(255, 255, 255, 0.74);
}

.conversation-form-row {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: 10px;
  padding: 10px 12px;
}

.conversation-form-row + .conversation-form-row {
  border-top: 1px solid rgba(31, 45, 26, 0.08);
}

.conversation-form-label {
  color: #6c7a64;
  font-size: 12px;
  font-weight: 600;
}

.conversation-form-value {
  color: #24311f;
  font-size: 13px;
  word-break: break-word;
}

.conversation-form-link {
  color: #2f63c8;
  text-decoration: none;
}

.conversation-time {
  color: #7b8a72;
}

.conversation-system-line {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(108, 126, 95, 0.10);
  color: #687660;
  font-size: 12px;
}

.conversation-system-text {
  max-width: 100%;
  word-break: break-word;
}

.conversation-system-time {
  color: #8b9883;
}
</style>
